// FLAG:useDocuAi — pure view-model for the Documents list-view analysis-state cell. Kept in its own
// file (no React/mui import graph) so its decision logic remains unit-testable in isolation.
// Reuses the SAME state source as the thumbnail tile (getInvoiceAnalysisState), so the
// list view and the grid always agree on what state a document is in.
import {
  getInvoiceAnalysisState,
  getInvoiceAnalysisLabel,
  INVOICE_ANALYSIS_STATE,
} from '@/modules/document/utils/invoice-analysis-state'

// Resolve what the list cell should display for a file. `value` lets ag-Grid pass a precomputed
// state (e.g. from a valueGetter) and short-circuit the derivation. Returns a flat view-model:
//   { state, label, isAnalyzing, isRetrying, hasValue }
// hasValue=false → render the muted placeholder; isAnalyzing=true → the animated "In Analyse" motion.
//
// FLAG:useDocuAi — `isRetrying` is deliberately NOT folded into `isAnalyzing`, even though RETRYING
// is also an in-flight state (analysisInProgress is still true). It answers a different question and
// drives two different things in the cell:
//   isAnalyzing → MOTION (the pulsing dot + the animated ellipsis after the label). Stays OFF for
//     RETRYING, mirroring the thumbnail tile, which stops its blink there on purpose
//     (analysisStatusButtonRetrying, plan docuai-analysis-retrying): "delayed — will be retried
//     automatically" is a WAITING state, not activity. Nothing is running right now; DocuAI is sitting
//     out a backoff gap that reaches 30 minutes. Animating it would claim work that isn't happening,
//     and a row full of pulses is no longer information.
//   isRetrying  → the DARK (blue `theme.primary`) dot background, which the AI sparkle has to be
//     inverted to white on — the same reason ANALYZING needs it. Without this the sparkle would be
//     blue on blue, i.e. invisible.
export const getAnalysisCellView = (data, value = null) => {
  const state = value || getInvoiceAnalysisState(data)
  const label = getInvoiceAnalysisLabel(state)

  if (!state || !label) {
    return { state: null, label: '', isAnalyzing: false, isRetrying: false, hasValue: false }
  }

  return {
    state,
    label,
    isAnalyzing: state === INVOICE_ANALYSIS_STATE.ANALYZING,
    isRetrying: state === INVOICE_ANALYSIS_STATE.RETRYING,
    hasValue: true,
  }
}

// Only the Documents list derives the state from `data` and therefore needs a clock tick to
// re-evaluate the selector's age cap. Expense/receipt callers pass a precomputed value and own
// their timeout already; adding another interval there would duplicate work without changing it.
export const shouldTickLiveDerivedAnalysisCell = (liveDerived, { isAnalyzing, isRetrying }) =>
  liveDerived === true && (isAnalyzing || isRetrying)
